import { useEffect, useState } from "react";
import { EmailSalesDoc, lastEmailed } from "../../components/EmailSalesDoc";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { depositCents, paths, type Estimate, type Invoice } from "@clevis/shared-types";
import { Button, Card, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { textMatches } from "../../lib/filter";
import { downloadCsv, csvDate, csvMoney } from "../../lib/csv";
import { useAuth } from "../../hooks/useAuth";
import { useEntitlements } from "../../hooks/useEntitlements";
import { SalesDocEditor } from "./SalesDocEditor";
import { PresentEstimate } from "./PresentEstimate";
import { DocHistory } from "../../components/DocHistory";
import { InvoiceSign } from "../../components/InvoiceSign";
import { LienWaivers } from "../../components/LienWaivers";
import { SalesSettingsPanel } from "../../components/SalesSettingsPanel";
import { useSalesSettings, useTeamMembers } from "../../hooks/useSalesSettings";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });

type EstRow = Estimate & {
  id: string;
  qbo?: { id?: string; lastSyncAt?: number; lastError?: string | null };
  qbd?: { status?: string; lastError?: string | null };
};
type InvRow = Invoice & {
  id: string;
  qbo?: { id?: string; lastSyncAt?: number; lastError?: string | null };
  qbd?: { status?: string; lastError?: string | null };
};

const estStatusStyle: Record<string, string> = {
  draft: "bg-steel/15 text-ink",
  sent: "bg-brandorange/15 text-brandorange-dark",
  accepted: "bg-success/15 text-success",
  declined: "bg-steel/10 text-steel line-through",
};
const invStatusStyle: Record<string, string> = {
  draft: "bg-steel/15 text-ink",
  sent: "bg-brandorange/15 text-brandorange-dark",
  partially_paid: "bg-brandorange/25 text-brandorange-dark",
  paid: "bg-success/15 text-success",
  void: "bg-steel/10 text-steel line-through",
  written_off: "bg-steel/15 text-steel",
};

/** What's still owed on an estimate's deposit (0 when none was asked for). */
const depositDue = (e: Estimate) => Math.max(0, depositCents(e.deposit, e.totalCents) - (e.depositPaidCents ?? 0));

/**
 * The estimates or invoices list. With `customerId` + `embedded` it's that
 * customer's list on their page, with every action (email, payments, void,
 * sign, PDF, history…) so nobody has to bounce over to the Invoices page.
 */
export function SalesPage({ kind, customerId, customerName, embedded = false }: { kind: "estimate" | "invoice"; customerId?: string; customerName?: string; embedded?: boolean }) {
  const { tenantId, role } = useAuth();
  const isAdmin = role === "admin" || role === "owner";
  const isManager = isAdmin || role === "manager";
  const salesSettings = useSalesSettings(tenantId);
  const team = useTeamMembers(tenantId);
  const [showSettings, setShowSettings] = useState(false);
  const [takingDeposit, setTakingDeposit] = useState<EstRow | null>(null);
  const [owning, setOwning] = useState<string | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const [signing, setSigning] = useState<string | null>(null);
  const [waiverFor, setWaiverFor] = useState<string | null>(null);
  // Writing off an invoice: which one, and why.
  const [writingOff, setWritingOff] = useState<string | null>(null);
  const [writeOffNote, setWriteOffNote] = useState("");
  const [estimates, setEstimates] = useState<EstRow[]>([]);
  const [invoices, setInvoices] = useState<InvRow[]>([]);
  const [editing, setEditing] = useState<"new" | EstRow | InvRow | null>(null);
  const [payingInvoice, setPayingInvoice] = useState<InvRow | null>(null);
  const [presenting, setPresenting] = useState<EstRow | null>(null);
  const [emailing, setEmailing] = useState<EstRow | InvRow | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [message, setMessage] = useState("");
  const { hasIntegration } = useEntitlements();
  const qboOn = hasIntegration("quickbooks-online");
  const qbdOn = hasIntegration("quickbooks-desktop");
  const [syncing, setSyncing] = useState<string | null>(null);

  async function syncToQb(invoiceId: string) {
    if (!tenantId) return;
    setSyncing(invoiceId);
    setMessage("");
    try {
      await call("syncInvoiceToQuickBooks", { tenantId, invoiceId });
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setSyncing(null);
    }
  }

  async function syncToQbd(invoiceId: string) {
    if (!tenantId) return;
    setSyncing(`d${invoiceId}`);
    setMessage("");
    try {
      await call("syncInvoiceToQbDesktop", { tenantId, invoiceId });
      setMessage("Queued — the Web Connector pushes it on its next check-in (usually under a minute).");
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setSyncing(null);
    }
  }

  // Estimates go to QuickBooks only when the company turned that on (QuickBooks page).
  const [sendEstimates, setSendEstimates] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => {
      setCompanyName((snap.get("name") as string) ?? "");
      setSendEstimates(snap.get("qbSendEstimates") === true);
    });
  }, [tenantId]);

  async function syncEstimate(estimateId: string, target: "online" | "desktop", allowResend = false) {
    if (!tenantId) return;
    setSyncing(`${target[0]}${estimateId}`);
    setMessage("");
    try {
      if (target === "online") {
        const res = await call<{ operation: string }>("syncEstimateToQuickBooks", { tenantId, estimateId });
        setMessage(res.operation === "update" ? "Estimate updated in QuickBooks." : "Estimate sent to QuickBooks.");
      } else {
        await call("syncEstimateToQbDesktop", { tenantId, estimateId, allowResend });
        setMessage("Queued: the Web Connector adds it on its next check-in (usually under a minute).");
      }
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setSyncing(null);
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    const col = kind === "estimate" ? paths.tenantEstimates(tenantId) : paths.tenantInvoices(tenantId);
    const q = customerId ? query(collection(db, col), where("customerId", "==", customerId), orderBy("createdAt", "desc")) : query(collection(db, col), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (kind === "estimate") setEstimates(rows as EstRow[]);
      else setInvoices(rows as InvRow[]);
    });
  }, [tenantId, kind, customerId]);

  async function act(fn: string, data: Record<string, unknown>) {
    setMessage("");
    try {
      const res = await call<{ invoiceId?: string }>(fn, { tenantId, ...data });
      if (fn === "convertEstimateToInvoice") setMessage("Converted — find it under Invoices.");
      return res;
    } catch (err) {
      setMessage((err as Error).message);
      return null;
    }
  }

  async function downloadPdf(docId: string) {
    setMessage("");
    try {
      const res = await call<{ base64: string; filename: string }>(
        "getSalesDocPdf",
        { tenantId, docId, kind },
        { mutation: false },
      );
      const bytes = Uint8Array.from(atob(res.base64), (ch) => ch.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = res.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  async function copySignLink(estimateId: string) {
    setMessage("");
    try {
      const res = await call<{ url: string }>("getEstimateSignLink", { tenantId, estimateId }, { mutation: false });
      await navigator.clipboard.writeText(res.url);
      setMessage("Signing link copied. The customer reads it, signs, and pays the deposit there. Emailing the estimate includes it too.");
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  async function setOwner(docId: string, uid: string) {
    setOwning(null);
    await act("setSalesDocOwner", { kind, docId, salespersonUid: uid || null });
  }

  // Who sold it: managers can change it after the document went out (commissions follow).
  const ownerLine = (row: EstRow | InvRow) =>
    owning === row.id ? (
      <span className="inline-block w-48 align-middle" onClick={(ev) => ev.stopPropagation()}>
        <Select className="w-48 py-0.5 text-xs" value={row.salespersonUid ?? ""} aria-label="Who sold it" onChange={(ev) => void setOwner(row.id, ev.target.value)}>
          <option value="">Nobody</option>
          {team.map((m) => (
            <option key={m.uid} value={m.uid}>
              {m.name}
            </option>
          ))}
        </Select>
      </span>
    ) : row.salespersonName || (isManager && row.status !== "draft") ? (
      <>
        {" · "}
        {isManager && row.status !== "draft" ? (
          <span
            role="button"
            tabIndex={0}
            className="underline decoration-dotted hover:text-ink"
            title="Change who sold it"
            onClick={(ev) => {
              ev.stopPropagation();
              setOwning(row.id);
            }}
          >
            {row.salespersonName || "no owner"}
          </span>
        ) : (
          row.salespersonName
        )}
      </>
    ) : null;

  async function copyPayLink(invoiceId: string) {
    setMessage("");
    try {
      const res = await call<{ url: string }>("createInvoicePayLink", { tenantId, invoiceId }, { mutation: false });
      await navigator.clipboard.writeText(res.url);
      setMessage("Payment link copied — text or email it to your customer.");
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  // One tap: the server builds the pay link and texts it to the customer.
  async function textPayLink(invoiceId: string) {
    setMessage("");
    try {
      await call("sendInvoiceText", { tenantId, invoiceId });
      setMessage("Pay link texted to the customer.");
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  const title = kind === "estimate" ? "Estimates" : "Invoices";
  const [search, setSearch] = useState("");
  const visibleEstimates = estimates.filter((e) =>
    textMatches(search, e.number, e.customerName, e.status, (e.totalCents / 100).toFixed(2)),
  );
  const visibleInvoices = invoices.filter((i) =>
    textMatches(search, i.number, i.customerName, i.status.replace("_", " "), (i.totalCents / 100).toFixed(2)),
  );

  function exportCsv() {
    if (kind === "estimate") {
      downloadCsv(
        "estimates",
        ["Number", "Customer", "Status", "Created", "Subtotal", "Tax", "Total"],
        visibleEstimates.map((e) => [
          e.number,
          e.customerName,
          e.status,
          csvDate(e.createdAt),
          csvMoney(e.subtotalCents),
          csvMoney(e.taxCents),
          csvMoney(e.totalCents),
        ]),
      );
    } else {
      downloadCsv(
        "invoices",
        ["Number", "Customer", "Status", "Created", "Due", "Total", "Paid", "Balance"],
        visibleInvoices.map((i) => [
          i.number,
          i.customerName,
          i.status,
          csvDate(i.createdAt),
          csvDate(i.dueAt),
          csvMoney(i.totalCents),
          csvMoney(i.amountPaidCents),
          csvMoney(i.totalCents - i.amountPaidCents),
        ]),
      );
    }
  }

  if (presenting) {
    // Keep presenting the live row so the status flips through to "done".
    const live = estimates.find((e) => e.id === presenting.id) ?? presenting;
    return <PresentEstimate estimate={live} companyName={companyName} onExit={() => setPresenting(null)} />;
  }

  if (editing) {
    return (
      <div className="mx-auto max-w-3xl">
        <SalesDocEditor kind={kind} existing={editing === "new" ? null : (editing as EstRow)} forCustomer={customerId ? { id: customerId, name: customerName ?? "" } : undefined} onDone={() => setEditing(null)} />
      </div>
    );
  }

  const count = kind === "estimate" ? estimates.length : invoices.length;
  return (
    <div className={embedded ? "space-y-2" : "mx-auto max-w-3xl space-y-4"}>
      {embedded ? (
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">
            {title} ({count})
          </h2>
          <Button variant="ghost" className="px-2.5 py-1 text-xs" onClick={() => setEditing("new")}>
            + New {kind}
          </Button>
        </div>
      ) : null}
      <div className={cn("flex items-center justify-between gap-2", embedded && "hidden")}>
        <h1 className="text-lg font-bold">{title}</h1>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <Button variant="secondary" onClick={() => setShowSettings((v) => !v)}>
              {kind === "estimate" ? "Types & terms" : "Invoice terms"}
            </Button>
          ) : null}
          <Button onClick={() => setEditing("new")}>New {kind}</Button>
        </div>
      </div>
      {showSettings && tenantId ? <SalesSettingsPanel tenantId={tenantId} settings={salesSettings} onClose={() => setShowSettings(false)} /> : null}
      <div className={cn("flex items-center gap-2", embedded && "hidden")}>
        <Input
          placeholder={`Filter ${title.toLowerCase()} — number, customer, status…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={exportCsv}>
          Export CSV
        </button>
      </div>
      {message ? <p className="text-xs text-ink">{message}</p> : null}

      {emailing && tenantId ? (
        <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "nearest" })}>
          <EmailSalesDoc
            key={emailing.id}
            tenantId={tenantId}
            kind={kind}
            docRow={emailing}
            onClose={() => setEmailing(null)}
            onDone={(msg) => {
              setEmailing(null);
              setMessage(msg);
            }}
          />
        </div>
      ) : null}

      <Card className="p-0">
        {kind === "estimate"
          ? visibleEstimates.map((e) => (
              <div key={e.id} className="border-b border-steel/10 last:border-0">
              <div className="flex items-center justify-between gap-3 px-4 py-2">
                <div
                  className="min-w-0 flex-1 cursor-pointer text-left"
                  role="button"
                  title={e.status === "draft" ? "Edit" : "Details and history"}
                  onClick={() => (e.status === "draft" ? setEditing(e) : setHistory(history === e.id ? null : e.id))}
                >
                  <div className="truncate text-sm font-medium text-ink">
                    #{e.number} · {e.customerName || "No customer"}
                    {e.typeName ? <span className="ml-1.5 rounded bg-steel/10 px-1.5 py-0.5 text-[10px] font-normal text-steel">{e.typeName}</span> : null}
                  </div>
                  <div className="text-xs text-steel">
                    {fmtDay(e.createdAt)} · {money(e.totalCents)}
                    {ownerLine(e)}
                    {depositCents(e.deposit, e.totalCents) > 0 && e.status !== "declined"
                      ? depositDue(e) > 0
                        ? ` · deposit ${money(depositDue(e))} due`
                        : " · deposit paid"
                      : ""}
                    {e.status === "accepted" && e.signedVia === "link" ? " · signed online" : ""}
                    {e.status === "declined" && e.declineNote ? ` · “${e.declineNote}”` : ""}
                    {lastEmailed(e.emails) ? (
                      <span className={/bounced|spam/.test(lastEmailed(e.emails, e.emailDelivery)) ? "text-danger" : ""}> · {lastEmailed(e.emails, e.emailDelivery)}</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={cn("rounded-full px-2 py-0.5 text-xs", estStatusStyle[e.status])}>
                    {e.status}
                  </span>
                  {sendEstimates && qboOn && e.status !== "draft" ? (
                    e.qbo?.lastError ? (
                      <button
                        className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger"
                        title={e.qbo.lastError}
                        onClick={() => void syncEstimate(e.id, "online")}
                      >
                        {syncing === `o${e.id}` ? "…" : "QB error: retry"}
                      </button>
                    ) : e.qbo?.id ? (
                      <button
                        className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success"
                        title="In QuickBooks. Click to update its status there."
                        onClick={() => void syncEstimate(e.id, "online")}
                      >
                        {syncing === `o${e.id}` ? "…" : "QB ✓"}
                      </button>
                    ) : (
                      <button className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => void syncEstimate(e.id, "online")}>
                        {syncing === `o${e.id}` ? "…" : "→ QB"}
                      </button>
                    )
                  ) : null}
                  {sendEstimates && qbdOn && e.status !== "draft" ? (
                    e.qbd?.status === "error" ? (
                      <button
                        className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger"
                        title={e.qbd.lastError ?? ""}
                        onClick={() => void syncEstimate(e.id, "desktop")}
                      >
                        {syncing === `d${e.id}` ? "…" : "QBD error: retry"}
                      </button>
                    ) : e.qbd?.status === "synced" ? (
                      <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success" title="In QuickBooks Desktop">
                        QBD ✓
                      </span>
                    ) : e.qbd?.status === "queued" ? (
                      <span className="rounded-full bg-brandorange/15 px-2 py-0.5 text-xs text-brandorange-dark">QBD queued…</span>
                    ) : (
                      <button className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => void syncEstimate(e.id, "desktop")}>
                        {syncing === `d${e.id}` ? "…" : "→ QBD"}
                      </button>
                    )
                  ) : null}
                  <button className="text-xs text-steel hover:text-ink" onClick={() => setHistory(history === e.id ? null : e.id)}>
                    History
                  </button>
                  <button className="text-xs text-steel hover:text-ink" onClick={() => void downloadPdf(e.id)}>
                    PDF
                  </button>
                  {e.status !== "declined" ? (
                    <button className="text-xs font-semibold text-brandorange-dark hover:text-brandorange" onClick={() => setEmailing(e)}>
                      Email
                    </button>
                  ) : null}
                  {e.status === "sent" ? (
                    <button className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => void copySignLink(e.id)} title="Copy the customer's signing link">
                      Sign link
                    </button>
                  ) : null}
                  {(e.status === "sent" || e.status === "accepted") && !e.invoiceId && depositDue(e) > 0 ? (
                    <button className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => setTakingDeposit(e)}>
                      Deposit
                    </button>
                  ) : null}
                  {e.status === "draft" || e.status === "sent" ? (
                    <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => setPresenting(e)}>
                      Present
                    </Button>
                  ) : null}
                  {e.status === "draft" ? (
                    <Button className="px-2.5 py-1 text-xs" onClick={() => void act("setEstimateStatus", { estimateId: e.id, action: "send" })}>
                      Send
                    </Button>
                  ) : null}
                  {e.status === "sent" ? (
                    <>
                      <Button className="px-2.5 py-1 text-xs" onClick={() => void act("setEstimateStatus", { estimateId: e.id, action: "accept" })}>
                        Accepted
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2.5 py-1 text-xs"
                        onClick={() => void act("setEstimateStatus", { estimateId: e.id, action: "decline" })}
                      >
                        Declined
                      </Button>
                    </>
                  ) : null}
                  {e.status === "accepted" && !e.invoiceId ? (
                    <Button
                      variant="secondary"
                      className="px-2.5 py-1 text-xs"
                      onClick={() => void act("convertEstimateToInvoice", { estimateId: e.id })}
                    >
                      → Invoice
                    </Button>
                  ) : null}
                </div>
              </div>
              {history === e.id && tenantId ? <DocHistory tenantId={tenantId} kind="estimate" doc={e} /> : null}
              </div>
            ))
          : visibleInvoices.map((inv) => (
              <div key={inv.id} className="border-b border-steel/10 last:border-0">
              <div className="flex items-center justify-between gap-3 px-4 py-2">
                <div
                  className="min-w-0 flex-1 cursor-pointer text-left"
                  role="button"
                  title={inv.status === "draft" ? "Edit" : "Details and history"}
                  onClick={() => (inv.status === "draft" ? setEditing(inv) : setHistory(history === inv.id ? null : inv.id))}
                >
                  <div className="truncate text-sm font-medium text-ink">
                    #{inv.number} · {inv.customerName || "No customer"}
                  </div>
                  <div className="text-xs text-steel">
                    {fmtDay(inv.createdAt)} · {money(inv.totalCents)}
                    {ownerLine(inv)}
                    {inv.amountPaidCents > 0 && inv.status !== "paid"
                      ? ` · ${money(inv.amountPaidCents)} paid`
                      : ""}
                    {inv.status === "written_off" && inv.writeOff ? ` · ${money(inv.writeOff.amountCents)} written off by ${inv.writeOff.byName || "the office"}: “${inv.writeOff.note}”` : ""}
                    {inv.signedAt ? ` · signed by ${inv.signerName ?? "the customer"}` : ""}
                    {lastEmailed(inv.emails) ? (
                      <span className={/bounced|spam/.test(lastEmailed(inv.emails, inv.emailDelivery)) ? "text-danger" : ""}> · {lastEmailed(inv.emails, inv.emailDelivery)}</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={cn("rounded-full px-2 py-0.5 text-xs", invStatusStyle[inv.status])}>
                    {inv.status.replace("_", " ")}
                  </span>
                  <button className="text-xs text-steel hover:text-ink" onClick={() => setHistory(history === inv.id ? null : inv.id)}>
                    History
                  </button>
                  <button className="text-xs text-steel hover:text-ink" onClick={() => void downloadPdf(inv.id)}>
                    PDF
                  </button>
                  {inv.status !== "draft" && inv.status !== "void" && !inv.signedAt ? (
                    <button className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => setSigning(signing === inv.id ? null : inv.id)} title="The customer signs on this screen">
                      Sign
                    </button>
                  ) : null}
                  {inv.status !== "draft" && inv.status !== "void" ? (
                    <button className="text-xs text-steel hover:text-ink" onClick={() => setWaiverFor(waiverFor === inv.id ? null : inv.id)} title="Nevada lien waiver for this invoice">
                      Lien waiver
                    </button>
                  ) : null}
                  {inv.status !== "void" ? (
                    <button className="text-xs font-semibold text-brandorange-dark hover:text-brandorange" onClick={() => setEmailing(inv)}>
                      Email
                    </button>
                  ) : null}
                  {qboOn && inv.status !== "draft" && inv.status !== "void" ? (
                    inv.qbo?.lastError ? (
                      <button
                        className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger"
                        title={inv.qbo.lastError}
                        onClick={() => void syncToQb(inv.id)}
                      >
                        {syncing === inv.id ? "…" : "QB error — retry"}
                      </button>
                    ) : inv.qbo?.id ? (
                      <button
                        className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success"
                        title={`Synced ${inv.qbo.lastSyncAt ? new Date(inv.qbo.lastSyncAt).toLocaleString() : ""} — click to re-sync payments`}
                        onClick={() => void syncToQb(inv.id)}
                      >
                        {syncing === inv.id ? "…" : "QB ✓"}
                      </button>
                    ) : (
                      <button
                        className="text-xs text-brandorange-dark hover:text-brandorange"
                        onClick={() => void syncToQb(inv.id)}
                      >
                        {syncing === inv.id ? "…" : "→ QB"}
                      </button>
                    )
                  ) : null}
                  {qbdOn && inv.status !== "draft" && inv.status !== "void" ? (
                    inv.qbd?.status === "error" ? (
                      <button
                        className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger"
                        title={inv.qbd.lastError ?? ""}
                        onClick={() => void syncToQbd(inv.id)}
                      >
                        {syncing === `d${inv.id}` ? "…" : "QBD error — retry"}
                      </button>
                    ) : inv.qbd?.status === "synced" ? (
                      <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success">QBD ✓</span>
                    ) : inv.qbd?.status === "queued" ? (
                      <span className="rounded-full bg-brandorange/15 px-2 py-0.5 text-xs text-brandorange-dark">
                        QBD queued…
                      </span>
                    ) : (
                      <button
                        className="text-xs text-brandorange-dark hover:text-brandorange"
                        onClick={() => void syncToQbd(inv.id)}
                      >
                        {syncing === `d${inv.id}` ? "…" : "→ QBD"}
                      </button>
                    )
                  ) : null}
                  {inv.status === "sent" || inv.status === "partially_paid" ? (
                    <>
                      <button
                        className="text-xs text-brandorange-dark hover:text-brandorange"
                        onClick={() => void copyPayLink(inv.id)}
                      >
                        Pay link
                      </button>
                      <button
                        className="text-xs text-brandorange-dark hover:text-brandorange"
                        onClick={() => void textPayLink(inv.id)}
                      >
                        Text it
                      </button>
                    </>
                  ) : null}
                  {inv.status === "draft" ? (
                    <Button className="px-2.5 py-1 text-xs" onClick={() => void act("setInvoiceStatus", { invoiceId: inv.id, action: "send" })}>
                      Send
                    </Button>
                  ) : null}
                  {inv.status === "sent" || inv.status === "partially_paid" ? (
                    <Button className="px-2.5 py-1 text-xs" onClick={() => setPayingInvoice(inv)}>
                      Record payment
                    </Button>
                  ) : null}
                  {isManager && (inv.status === "sent" || inv.status === "partially_paid") ? (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      title="Comped or uncollectible: take the rest off what's owed"
                      onClick={() => {
                        setWritingOff(writingOff === inv.id ? null : inv.id);
                        setWriteOffNote("");
                      }}
                    >
                      Write off
                    </Button>
                  ) : null}
                  {isManager && inv.status === "written_off" ? (
                    <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void act("undoWriteOff", { invoiceId: inv.id })}>
                      Undo write-off
                    </Button>
                  ) : null}
                  {inv.status !== "paid" && inv.status !== "void" && inv.status !== "written_off" ? (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() => void act("setInvoiceStatus", { invoiceId: inv.id, action: "void" })}
                    >
                      Void
                    </Button>
                  ) : null}
                </div>
              </div>
              {writingOff === inv.id ? (
                <form
                  className="mx-4 mb-2 flex flex-wrap items-end gap-2 rounded-lg border border-steel/20 bg-cream/40 p-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void act("writeOffInvoice", { invoiceId: inv.id, note: writeOffNote }).then((r) => {
                      if (r) {
                        setWritingOff(null);
                        setMessage(`Invoice #${inv.number}: ${money(inv.totalCents - inv.amountPaidCents)} written off.`);
                      }
                    });
                  }}
                >
                  <div className="min-w-56 flex-1">
                    <label className="mb-1 block text-xs font-medium text-ink/80">
                      Write off {money(inv.totalCents - inv.amountPaidCents)}: why? (comped, warranty, uncollectible…)
                    </label>
                    <Input className="py-1 text-sm" autoFocus value={writeOffNote} maxLength={500} placeholder="e.g. Comped: second trip was our fault" onChange={(e) => setWriteOffNote(e.target.value)} />
                  </div>
                  <Button type="submit" className="px-3 py-1 text-xs" disabled={writeOffNote.trim().length < 2}>
                    Write it off
                  </Button>
                  <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setWritingOff(null)}>
                    Cancel
                  </Button>
                  <p className="w-full text-[11px] text-steel">The invoice stays on record with what was paid; the rest comes off what's owed. You can undo it.</p>
                </form>
              ) : null}
              {signing === inv.id && tenantId ? (
                <div className="mx-4 mb-2">
                  <InvoiceSign
                    tenantId={tenantId}
                    invoiceId={inv.id}
                    number={inv.number}
                    totalCents={inv.totalCents}
                    defaultName={inv.customerName}
                    onCancel={() => setSigning(null)}
                    onDone={(n) => {
                      setSigning(null);
                      setMessage(`Invoice #${inv.number} signed by ${n}.`);
                    }}
                  />
                </div>
              ) : null}
              {history === inv.id && tenantId ? <DocHistory tenantId={tenantId} kind="invoice" doc={inv} /> : null}
              {waiverFor === inv.id && tenantId ? <LienWaivers tenantId={tenantId} invoice={inv} companyName={companyName} /> : null}
              </div>
            ))}
        {(kind === "estimate" ? estimates : invoices).length === 0 ? (
          <div className={cn("px-4 text-center text-sm text-steel", embedded ? "py-3 text-xs" : "py-8")}>{embedded ? `No ${title.toLowerCase()} for this customer yet.` : `No ${title.toLowerCase()} yet.`}</div>
        ) : null}
      </Card>

      {takingDeposit ? (
        <PaymentModal
          title={`Record deposit — estimate #${takingDeposit.number}`}
          remainingCents={depositDue(takingDeposit)}
          remainingLabel="deposit due"
          onClose={() => setTakingDeposit(null)}
          onRecord={async (amountCents, method, note) => {
            const ok = await act("recordEstimateDeposit", { estimateId: takingDeposit.id, amountCents, method, note });
            if (ok) {
              setTakingDeposit(null);
              setMessage(`Deposit recorded on estimate #${takingDeposit.number}. It moves onto the invoice when you convert it.`);
            }
          }}
        />
      ) : null}

      {payingInvoice ? (
        <PaymentModal
          title={`Record payment — #${payingInvoice.number}`}
          remainingCents={payingInvoice.totalCents - payingInvoice.amountPaidCents}
          onClose={() => setPayingInvoice(null)}
          onRecord={async (amountCents, method, note) => {
            const ok = await act("recordInvoicePayment", {
              invoiceId: payingInvoice.id,
              amountCents,
              method,
              note,
            });
            if (ok) setPayingInvoice(null);
          }}
        />
      ) : null}
    </div>
  );
}

function PaymentModal({
  title,
  remainingCents: remaining,
  remainingLabel = "remaining",
  onClose,
  onRecord,
}: {
  title: string;
  remainingCents: number;
  remainingLabel?: string;
  onClose: () => void;
  onRecord: (amountCents: number, method: string, note: string) => Promise<void>;
}) {
  const [amount, setAmount] = useState((remaining / 100).toFixed(2));
  const [method, setMethod] = useState("check");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-ink/30 p-4" onClick={onClose}>
      <div className="w-full max-w-xs rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-sm font-bold text-ink">{title}</h2>
        <p className="mb-3 text-xs text-steel">
          {money(remaining)} {remainingLabel}
        </p>
        <div className="space-y-3">
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="card">Card</option>
              <option value="ach">ACH / bank</option>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                await onRecord(Math.round(parseFloat(amount || "0") * 100), method, note);
                setBusy(false);
              }}
            >
              Record
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
